import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import {
  DEFAULT_BUYER_INTEREST_RANGE,
  trackBuyerInterestEvent,
} from "../../constants/automapDealerBuyerInterestConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { fetchDealerBuyerInterestSummary } from "../../services/automapDealerBuyerInterestApi";

function SummaryKpi({ label, value, hint }) {
  return (
    <article className="dealer-kpi-card">
      <span className="dealer-kpi-card__label">{label}</span>
      <strong className="dealer-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="dealer-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

export default function DealerBuyerInterestSummaryWidget({ dealerId, range = DEFAULT_BUYER_INTEREST_RANGE }) {
  const { t, formatMoney } = useDealerPortalIntl();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerBuyerInterestSummary(dealerId, { range });
      setSummary(data);
      trackBuyerInterestEvent("dealer_buyer_interest_viewed", { dealerId, range, section: "dashboard_widget" });
    } catch (err) {
      setSummary(null);
      setError(err.message || t("buyerInterest.loadError"));
    } finally {
      setLoading(false);
    }
  }, [dealerId, range, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <section className="dealer-panel" aria-busy="true">
        <h3>{t("buyerInterest.title")}</h3>
        <p className="muted">{t("common.loading")}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="dealer-panel dealer-panel--error">
        <h3>{t("buyerInterest.title")}</h3>
        <p className="muted">{error}</p>
        <button type="button" className="dealer-portal-btn" onClick={load}>
          {t("common.retry")}
        </button>
      </section>
    );
  }

  const activeCount = summary?.summary?.activeInventoryCount ?? 0;
  const stats = summary?.summary || {};
  const topListings = summary?.topListings || [];

  if (activeCount === 0) {
    return (
      <section className="dealer-panel">
        <div className="dealer-panel__header">
          <h3>{t("buyerInterest.title")}</h3>
          <Link to={automapDealerPortalPath("inventario/demanda")}>{t("buyerInterest.viewDemandPanel")}</Link>
        </div>
        <p className="muted">{t("buyerInterest.emptyInventoryTitle")}</p>
        <p className="muted">{t("buyerInterest.emptyInventoryBody")}</p>
      </section>
    );
  }

  const hasAnyInterest =
    (stats.totalLikes || 0) > 0 || (stats.totalCompares || 0) > 0 || (stats.activePriceWatchers || 0) > 0;

  return (
    <section className="dealer-panel">
      <div className="dealer-panel__header">
        <h3>{t("buyerInterest.title")}</h3>
        <Link to={automapDealerPortalPath("inventario/demanda")}>{t("buyerInterest.viewDemandPanel")}</Link>
      </div>
      <p className="dealer-panel__footnote muted">{t("buyerInterest.privacyNote")}</p>

      {!hasAnyInterest ? (
        <>
          <p>{t("buyerInterest.emptyInterestTitle")}</p>
          <p className="muted">{t("buyerInterest.emptyInterestBody")}</p>
        </>
      ) : (
        <>
          <div className="dealer-kpi-grid dealer-kpi-grid--compact">
            <SummaryKpi label={t("buyerInterest.kpiLikes")} value={stats.totalLikes} />
            <SummaryKpi label={t("buyerInterest.kpiCompares")} value={stats.totalCompares} />
            <SummaryKpi label={t("buyerInterest.kpiWatchers")} value={stats.activePriceWatchers} />
            <SummaryKpi label={t("buyerInterest.kpiRising")} value={stats.risingListingsCount} />
          </div>
          {stats.topLikedVehicle?.title ? (
            <p className="muted">
              {t("buyerInterest.topLikedVehicle")}: <strong>{stats.topLikedVehicle.title}</strong>
              {stats.topLikedVehicle.likeCountLabel ? ` · ${stats.topLikedVehicle.likeCountLabel}` : ""}
            </p>
          ) : null}
          <h4>{t("buyerInterest.topFiveTitle")}</h4>
          {topListings.length === 0 ? (
            <p className="muted">{t("buyerInterest.emptyInterestBody")}</p>
          ) : (
            <ul className="dealer-top-listings">
              {topListings.map((listing) => (
                <li key={listing.listingId} className="dealer-top-listing">
                  {listing.thumbnailUrl ? (
                    <img src={listing.thumbnailUrl} alt="" className="dealer-top-listing__thumb" />
                  ) : (
                    <div className="dealer-top-listing__thumb dealer-top-listing__thumb--empty" />
                  )}
                  <div className="dealer-top-listing__copy">
                    <strong>{listing.title}</strong>
                    <p className="muted">
                      {t("buyerInterest.likes")}: {listing.likeCountLabel || listing.likeCount || "0"} ·{" "}
                      {t("buyerInterest.watchers")}: {listing.watchCountLabel || listing.watchCount || "0"}
                      {listing.priceCop ? ` · ${formatMoney(listing.priceCop)}` : ""}
                    </p>
                  </div>
                  <Link
                    className="dealer-portal-btn"
                    to={automapDealerPortalPath(`inventario/${listing.listingId}`)}
                    onClick={() =>
                      trackBuyerInterestEvent("dealer_buyer_interest_listing_clicked", {
                        dealerId,
                        listingId: listing.listingId,
                      })
                    }
                  >
                    {t("common.view")}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
